<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Depósitos</h1>
          <p class="view-header__subtitle">Depósitos donde se registra y mueve el stock por sucursal.</p>
        </div>
        <div class="view-header__actions">
          <button type="button" class="btn view-header__btn view-header__btn--primary" @click="openNewWarehouse">
            <i class="bi bi-plus-lg"></i>
            Nuevo depósito
          </button>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="doSearch" type="search" class="form-control" placeholder="Buscar depósito...">
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
        @edit="openEditWarehouse"
        @delete="deleteWarehouse"
      />
    </div>

    <!-- MODAL NUEVO / EDITAR DEPÓSITO -->
    <transition name="modal">
      <div v-if="showWarehouseModal" class="modal-mask-product">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="form-card__header">
              <h6 class="form-card__title">
                <i :class="warehouse.id == null ? 'bi bi-plus-lg' : 'bi bi-pencil'"></i>
                {{ warehouse.id == null ? 'Nuevo depósito' : 'Editar depósito' }}
              </h6>
              <button type="button" class="form-card__btn" @click="closeWarehouseModal">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <div class="modal-body form-card form-card--bare">
              <div class="form-card__body">
                <div class="form-card__field">
                  <label class="form-card__label" for="warehouse-name">Nombre</label>
                  <input
                    id="warehouse-name"
                    v-model.trim="warehouse.name"
                    type="text"
                    class="form-control"
                    placeholder="Nombre del depósito"
                    :class="{ 'is-invalid': nameState === false }"
                  >
                  <div v-if="nameState === false" class="form-card__error">El nombre debe tener al menos 3 letras.</div>
                  <div v-else class="form-card__hint">Campo obligatorio.</div>
                </div>

                <div class="form-card__field">
                  <label class="form-card__label" for="warehouse-bo">Sucursal</label>
                  <q-select v-model="warehouse.bo_id" :options="branchofficeOptions" placeholder="Ninguna" />
                </div>
              </div>
            </div>
            <div class="form-card__footer">
              <button type="button" class="form-card__btn" @click="closeWarehouseModal">Cancelar</button>
              <button
                type="button"
                class="form-card__btn form-card__btn--primary"
                :disabled="nameState === false || !warehouse.name"
                @click="saveWarehouse"
              >
                <i class="bi bi-check-lg"></i>
                Guardar depósito
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import QSelect from '@/components/shared/QSelect.vue';

export default {
  components: {
    VdtnetTable,
    QSelect,
  },
  data: function () {
    return {
      doSearch: '',
      isLoading: false,
      showWarehouseModal: false,
      warehouse: {
        id: null,
        name: "",
        bo_id: null,
      },
      emptyWarehouse: {
        id: null,
        name: "",
        bo_id: null,
      },
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/warehouses/list",
          type: "GET",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        autoWidth: false,
        pageLength: 15,
        searching: true,
      },
      fields: {
        name: {
          label: "Nombre",
          searchable: true,
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        sucursal: { label: "Sucursal", searchable: true },
        actions: {
          isLocal: true,
          label: "Acciones",
          width: "100px",
          align: "right",
          orderable: false,
          searchable: false,
          render: () => `
            <div class="table-action-group">
              <button data-action="edit" class="table-action-btn" type="button" title="Editar">
                <i class="bi bi-pencil"></i>
              </button>
              <button data-action="delete" class="table-action-btn" type="button" title="Borrar">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          `,
        },
      },
    };
  },
  computed: {
    ...mapState('branchoffices', ['branchoffices']),
    branchofficeOptions() {
      return this.branchoffices.map(b => ({ value: b.id, label: b.name }));
    },
    nameState() {
      const len = (this.warehouse.name || '').length;
      return len < 3 && len > 0 ? false : null;
    },
  },
  watch: {
    doSearch(newValue, oldValue) {
      if (newValue.length >= 4 || oldValue.length >= 4) {
        this.$refs.table.search(this.doSearch);
      }
      if (newValue === '' && oldValue.length > 0) {
        this.$refs.table.search(this.doSearch);
      }
    },
  },
  mounted() {
    this.$store.dispatch('branchoffices/getAllBranchoffices');
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isLoading = true; });
      dt.on('xhr.dt', () => { this.isLoading = false; });
    });
  },
  methods: {
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    openNewWarehouse() {
      this.warehouse = Object.assign({}, this.emptyWarehouse);
      this.showWarehouseModal = true;
    },
    openEditWarehouse(data) {
      this.isLoading = true;
      this.$http.get("/api/warehouses/getWarehouse/" + data.id).then((response) => {
        this.warehouse = response.data.data;
        this.isLoading = false;
        this.showWarehouseModal = true;
      });
    },
    closeWarehouseModal() {
      this.showWarehouseModal = false;
      this.warehouse = Object.assign({}, this.emptyWarehouse);
    },
    validateForm() {
      if (!this.warehouse.name || this.warehouse.name.trim() === "") {
        alert("Debe ingresar un nombre");
        return false;
      }
      if (this.warehouse.name.length < 3) {
        alert("El nombre debe tener al menos 3 letras");
        return false;
      }
      return true;
    },
    saveWarehouse() {
      if (!this.validateForm()) return;
      this.isLoading = true;
      const isEdit = this.warehouse.id != null;
      const url = isEdit
        ? "/api/warehouses/edit/" + this.warehouse.id
        : "/api/warehouses/add";
      this.$http.post(url, this.warehouse).then(() => {
        this.closeWarehouseModal();
        this.refreshTable();
      }).finally(() => {
        this.isLoading = false;
      });
    },
    deleteWarehouse(data) {
      if (!confirm("¿Está seguro que desea borrar este depósito?")) return;
      this.isLoading = true;
      this.$http.post("/api/warehouses/delete", { id: data.id })
        .then((response) => {
          if (response.data.code == 201) {
            this.refreshTable();
          } else {
            this.isLoading = false;
          }
        })
        .catch((error) => {
          console.log(error.message);
          alert('Error al borrar');
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.modal-mask-product {
  position: fixed;
  z-index: 1050;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(16, 24, 40, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
}

.modal-dialog {
  margin: 0;
  width: 100%;
  max-width: 560px;
}

.modal-content {
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
  overflow: hidden;
  background: var(--surface);
}

.modal-body {
  max-height: 80vh;
  overflow-y: auto;
  padding: 0;
}

.modal-enter,
.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-content,
.modal-leave-active .modal-content {
  transform: scale(1.02);
}
</style>
